import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Camera, Keyboard } from 'lucide-react';

const QRScanner = ({ onScan, onClose }) => {
  const scannerRef = useRef(null);
  const scannerInstanceRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [manualCode, setManualCode] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);

  useEffect(() => {
    let isActive = true;
    let isScannerRunning = false;

    const startScanner = async () => {
      try {
        // Prevent multiple initializations with a more robust check
        if (scannerInstanceRef.current) {
          console.log('Scanner already initialized, skipping');
          return;
        }

        // Add a small delay to ensure DOM is ready and prevent race conditions
        await new Promise(resolve => setTimeout(resolve, 100));
        
        if (!isActive) return;

        // Check if scanner already exists and clear it
        const existingElement = document.getElementById('qr-reader');
        if (existingElement) {
          // Check if already has camera elements
          const existingVideo = existingElement.querySelector('video');
          if (existingVideo) {
            console.log('Camera already active, skipping initialization');
            return;
          }
          existingElement.innerHTML = '';
        }

        const html5QrCode = new Html5Qrcode('qr-reader');
        scannerInstanceRef.current = html5QrCode;
        
        if (!isActive) {
          scannerInstanceRef.current = null;
          return;
        }
        
        // Set running flag BEFORE starting
        isScannerRunning = true;
        
        await html5QrCode.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
          },
          (decodedText) => {
            if (isActive && scannerInstanceRef.current && isScannerRunning) {
              isScannerRunning = false;
              onScan(decodedText);
              scannerInstanceRef.current.stop().catch((err) => {
                console.warn('Error stopping scanner after scan:', err);
              });
              scannerInstanceRef.current = null;
            }
          },
          (errorMessage) => {
            // Ignore scan errors, they happen constantly
          }
        );
        
        if (isActive) {
          setScanning(true);
        }
      } catch (err) {
        if (isActive) {
          const errorMsg = err?.message || err?.toString() || 'Unbekannter Fehler';
          setError('Kamera konnte nicht gestartet werden: ' + errorMsg);
          console.error('QR Scanner error:', err);
        }
        scannerInstanceRef.current = null;
        isScannerRunning = false;
      }
    };

    // Use a timeout to ensure only one initialization happens
    const timeoutId = setTimeout(() => {
      if (isActive && !scannerInstanceRef.current) {
        startScanner();
      }
    }, 50);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
      
      if (scannerInstanceRef.current) {
        const currentScanner = scannerInstanceRef.current;
        scannerInstanceRef.current = null;
        
        if (isScannerRunning) {
          try {
            currentScanner.stop().catch((err) => {
              console.warn('Scanner cleanup error (safe to ignore):', err.message);
            });
          } catch (err) {
            console.warn('Scanner cleanup error (safe to ignore):', err.message);
          }
        }
        isScannerRunning = false;
      }
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" data-testid="qr-scanner-overlay">
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-2">
            <Camera className="text-blue-600" size={24} />
            <h2 className="text-xl font-bold text-slate-900">QR-Code scannen</h2>
          </div>
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            data-testid="close-qr-scanner"
          >
            <X size={20} />
          </Button>
        </div>

        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800" data-testid="qr-scanner-error">
            {error}
            {/* Show manual input when camera fails */}
            <div className="mt-4 pt-4 border-t border-red-200">
              <p className="text-sm text-slate-700 mb-2">Code manuell eingeben:</p>
              <div className="flex gap-2">
                <Input
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  placeholder="Code eingeben..."
                  className="flex-1"
                  data-testid="manual-code-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && manualCode.trim()) {
                      onScan(manualCode.trim());
                    }
                  }}
                />
                <Button
                  onClick={() => {
                    if (manualCode.trim()) {
                      onScan(manualCode.trim());
                    }
                  }}
                  disabled={!manualCode.trim()}
                  data-testid="submit-manual-code"
                >
                  OK
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div
            id="qr-reader"
            className="rounded-lg overflow-hidden"
            data-testid="qr-reader-video"
          />
        )}

        <p className="text-sm text-slate-600 mt-4 text-center">
          Halte den QR-Code vor die Kamera
        </p>
        
        {/* Manual input toggle - always available */}
        <div className="mt-4 pt-4 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowManualInput(!showManualInput)}
            className="w-full"
            data-testid="toggle-manual-input"
          >
            <Keyboard size={16} className="mr-2" />
            {showManualInput ? 'Manuelle Eingabe ausblenden' : 'Code manuell eingeben'}
          </Button>
          
          {showManualInput && (
            <div className="mt-3 flex gap-2">
              <Input
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                placeholder="Code eingeben..."
                className="flex-1"
                data-testid="manual-code-input-alt"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && manualCode.trim()) {
                    onScan(manualCode.trim());
                  }
                }}
              />
              <Button
                onClick={() => {
                  if (manualCode.trim()) {
                    onScan(manualCode.trim());
                  }
                }}
                disabled={!manualCode.trim()}
                data-testid="submit-manual-code-alt"
              >
                OK
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRScanner;